export class QuantityAdjuster extends window.BAO.CustomElement() {
  static get observedAttributes () {
    return ['value', 'disabled', 'min', 'max']
  }

  constructor () {
    super()
    this.setup()
  }

  /**
   * Lifecycle hook when element is added to the DOM.
   * Ensures initial values are set and select options are populated if applicable.
   *
   * @returns {void}
   */
  connectedCallback () {
    super.connectedCallback()

    if (!this.hasAttribute('value')) {
      this.setAttribute('value', this.min.toString())
    }

    if (!this.hasAttribute('min')) {
      this.setAttribute('value', this.min.toString())
    }

    this._upgradeProperty('value')
    this._upgradeProperty('disabled')

    if (this.select) {
      this.populateSelectOptions()
    }
  }

  /**
   * Handles changes to observed attributes.
   *
   * @param {string} name - The attribute name.
   * @param {string|null} oldValue - The previous value.
   * @param {string|null} newValue - The new value.
   *
   * @returns {void}
   */
  attributeChangedCallback (name, oldValue, newValue) {
    const isANumber = isNaN(parseInt(newValue, 10)) === false

    switch (name) {
      case 'value': {
        if (!isANumber) break
        if (parseInt(oldValue, 10) === parseInt(newValue, 10)) break
        this.handleValueChangeSideEffects(parseInt(newValue, 10))
        break
      }

      case 'min': {
        if (!isANumber) break
        if (this.value < parseInt(newValue, 10)) {
          this.value = parseInt(newValue, 10)
        }
        if (this.select) this.populateSelectOptions()
        break
      }

      case 'max': {
        if (!isANumber) break
        const newMax = parseInt(newValue, 10)
        if (this.value > newMax) {
          this.value = newMax
        }

        if (this.input && this.plusButton) {
          this.plusButton.disabled = this.value >= newMax
        }

        if (this.select) this.populateSelectOptions()
        break
      }

      case 'disabled': {
        const hasValue = newValue != null
        if (this.input) {
          this.minusButton.disabled = hasValue
          this.plusButton.disabled = hasValue
          this.input.disabled = hasValue
        }
        if (this.select) {
          this.select.disabled = hasValue
        }
        break
      }
    }
  }

  /**
   * Sets up initial input/select values and states.
   *
   * @returns {void}
   */
  setup () {
    if (this.input) {
      this.input.setAttribute('value', this.value.toString())
      this.input.value = this.value.toString()
      this.input.name = this.name

      if (this.value === this.min) {
        this.minusButton.disabled = true
      }
    }

    if (this.select) {
      this.select.value = this.value.toString()
      this.select.name = this.name
    }
  }

  /**
   * Attaches event listeners depending on whether input or select is used.
   *
   * @returns {void}
   */
  setupListeners () {
    super.setupListeners()

    if (this.input) {
      this.onMinusClick = this.onMinusClick.bind(this)
      this.onPlusClick = this.onPlusClick.bind(this)
      this.onInputChange = this.onInputChange.bind(this)
      this.onInputInput = this.onInputInput.bind(this)

      this.listeners.add(this.minusButton, 'click', this.onMinusClick)
      this.listeners.add(this.plusButton, 'click', this.onPlusClick)
      this.listeners.add(this.input, 'change', this.onInputChange)
      this.listeners.add(this.input, 'input', this.onInputInput)
    }

    if (this.select) {
      this.onSelectChange = this.onSelectChange.bind(this)
      this.listeners.add(this.select, 'change', this.onSelectChange)
    }
  }

  /**
   * Handles minus button click.
   *
   * @returns {void}
   */
  onMinusClick () {
    if (this.value === this.min) return
    this.value -= 1
  }

  /**
   * Handles plus button click.
   *
   * @returns {void}
   */
  onPlusClick () {
    this.value += 1
  }

  /**
   * Handles input change event.
   *
   * @param {Event} e
   * @returns {void}
   */
  onInputChange (e) {
    if (isNaN(e.target.valueAsNumber) === false) {
      this.value = e.target.valueAsNumber
    }
  }

  /**
   * Handles input typing event.
   *
   * @param {InputEvent} e
   * @returns {void}
   */
  onInputInput (e) {
    if (isNaN(e.target.valueAsNumber) === false) {
      this.value = e.target.valueAsNumber
    }
  }

  /**
   * Handles select change event.
   *
   * @param {Event} e
   * @returns {void}
   */
  onSelectChange (e) {
    const newVal = parseInt(e.target.value, 10)
    if (!isNaN(newVal)) {
      this.value = newVal
    }
  }

  /**
   * Populates select options based on min/max, capped at 50 units aligned with Shopify line item limits.
   *
   * @returns {void}
   */
  populateSelectOptions () {
    if (!this.select) return

    this.select.innerHTML = ''

    const displayMax = Math.min(this.max || this.min + 50, 50)

    for (let i = this.min; i <= displayMax; i++) {
      const option = document.createElement('option')
      option.value = i.toString()
      option.textContent = i.toString()
      if (i === this.value) option.selected = true
      this.select.appendChild(option)
    }
  }

  /**
   * Handles UI and state updates when value changes.
   *
   * @param {number} val
   * @returns {void}
   */
  handleValueChangeSideEffects (val) {
    const oldVal = this.value
    if (oldVal === val) return

    if (this.input) {
      this.input.value = val.toString()
      this.minusButton.disabled = val === this.min
      this.plusButton.disabled = this.max != null && val === this.max
    }

    if (this.select) {
      this.select.value = val.toString()
    }

    this.dispatchEvent(
      new CustomEvent(window.BAO.EVENTS.QUANTITY_ADJUSTER.CHANGE, {
        detail: {
          oldValue: oldVal,
          value: val,
        },
        bubbles: true,
      }),
    )
  }

  /**
   * @param {Number} val
   *
   * @returns {void}
   */
  set value (val) {
    if (val < this.min) {
      val = this.min
    }

    this.handleValueChangeSideEffects(val)

    this.setAttribute('value', val.toString())
  }

  /**
   * Gets quantity value.
   *
   * @returns {number}
   */
  get value () {
    return parseInt(this.getAttribute('value'), 10)
  }

  /**
   * Gets minimum allowed value.
   *
   * @returns {number}
   */
  get min () {
    return parseInt(this.getAttribute('min'), 10) || 0
  }

  /**
   * Gets maximum allowed value.
   *
   * @returns {number|null}
   */
  get max () {
    return parseInt(this.getAttribute('max'), 10) || null
  }

  /**
   * Gets input name.
   *
   * @returns {string}
   */
  get name () {
    return this.getAttribute('name') || 'quantity'
  }

  /**
   * Gets minus button element.
   *
   * @returns {HTMLButtonElement|undefined}
   */
  get minusButton () {
    return this.els.minus?.element
  }

  /**
   * Gets plus button element.
   *
   * @returns {HTMLButtonElement|undefined}
   */
  get plusButton () {
    return this.els.plus?.element
  }

  /**
   * Gets input element.
   *
   * @returns {HTMLInputElement|undefined}
   */
  get input () {
    return this.els.input?.element
  }

  /**
   * Gets select element.
   *
   * @returns {HTMLSelectElement|undefined}
   */
  get select () {
    return this.els.select?.element
  }
}

if (!customElements.get('quantity-adjuster')) {
  customElements.define('quantity-adjuster', QuantityAdjuster)
}
